import { useEffect } from "react";
import { Toaster } from "@/components/ui/sonner";
import useReveal from "@/hooks/useReveal";
import { setPageSEO } from "@/lib/seo";
import Header from "@/components/landing/Header";
import Footer from "@/components/landing/Footer";
import WhatsAppFloat from "@/components/landing/WhatsAppFloat";
import SubpageHero from "@/components/landing/SubpageHero";
import WhatEOSAchieves from "@/components/landing/WhatEOSAchieves";
import SixComponents from "@/components/landing/SixComponents";
import FinalCTA from "@/components/landing/FinalCTA";
import { Layers, Users2, TrendingUp } from "lucide-react";

const PHILOSOPHY = [
  {
    icon: Layers,
    num: "01",
    title: "Simple pero no simplista",
    text: "EOS no es una teoría académica. Son seis componentes que cualquier empresario puede entender y aplicar desde el primer día. La simplicidad es parte del diseño — no una limitación. Menos herramientas, más foco.",
  },
  {
    icon: Users2,
    num: "02",
    title: "Empieza por el liderazgo",
    text: "EOS no se implementa desde abajo hacia arriba. Empieza con el equipo directivo: cuando los líderes operan alineados y con accountability real, el cambio se extiende naturalmente al resto de la organización.",
  },
  {
    icon: TrendingUp,
    num: "03",
    title: "El ritmo lo hace sostenible",
    text: "EOS instala una cadencia de reuniones, revisiones y ajustes que convierte la disciplina en un hábito. No es un proyecto puntual — es una forma de trabajar que se consolida y mejora trimestre a trimestre.",
  },
];

export default function ElModeloEOS() {
  useReveal();

  useEffect(() => {
    setPageSEO({
      title: "El Modelo EOS | Los 6 Componentes de EOS | Carlos Garcín Chile",
      description: "Conoce el Modelo EOS y sus 6 componentes: Visión, Personas, Datos, Asuntos, Procesos y Tracción. Usado por más de 34.000 empresas en el mundo para escalar.",
      path: "/el-modelo-eos",
      keywords: "modelo EOS, 6 componentes EOS, Visión Personas Datos Asuntos Procesos Tracción",
    });
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <Header />
      <main>
        <SubpageHero
          eyebrow="El Modelo EOS — Entrepreneurial Operating System"
          title={<>Los 6 componentes que <span className="text-[#F26522]">ordenan tu empresa</span></>}
          subtitle="EOS es un conjunto completo e integrado de conceptos, herramientas y prácticas que fortalecen los seis componentes clave de cualquier negocio. Sencillo de entender, poderoso en la práctica."
          withCta
        />

        {/* Los 3 pilares */}
        <WhatEOSAchieves />

        {/* El wheel de los 6 componentes — pieza gráfica central */}
        <SixComponents />

        {/* La filosofía detrás del modelo */}
        <section data-testid="modelo-philosophy" className="section-padding bg-white">
          <div className="container-eos">
            <div className="max-w-3xl reveal">
              <span className="eyebrow">La filosofía detrás del modelo</span>
              <h2 className="mt-3 font-display font-extrabold text-3xl sm:text-4xl lg:text-5xl text-navy-800 leading-tight">
                Por qué EOS funciona{" "}
                <span className="text-[#F26522]">cuando otros marcos no lo hacen</span>
              </h2>
              <p className="mt-5 text-lg text-slate-600 max-w-2xl">
                No es el único marco de gestión empresarial que existe. Pero hay tres razones por las que tiene un impacto distinto en los equipos que lo adoptan.
              </p>
            </div>
            <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
              {PHILOSOPHY.map((p, idx) => (
                <article
                  key={idx}
                  data-testid={`philosophy-card-${idx + 1}`}
                  className="reveal relative rounded-2xl border border-slate-200 bg-white p-8 transition-all duration-300 hover:border-[#F26522]/40 hover:shadow-xl hover:-translate-y-1 overflow-hidden"
                  style={{ transitionDelay: `${idx * 90}ms` }}
                >
                  <div className="absolute top-0 inset-x-0 h-1 bg-gradient-to-r from-[#F26522] to-[#F26522]/40" />
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-lg bg-navy-800 flex items-center justify-center shadow-md">
                      <p.icon className="w-5 h-5 text-white" strokeWidth={1.7} />
                    </div>
                    <span className="text-xs font-bold tracking-[0.2em] uppercase text-[#F26522]">
                      {p.num} / 03
                    </span>
                  </div>
                  <h3 className="mt-6 font-display font-extrabold text-2xl text-navy-800">{p.title}</h3>
                  <p className="mt-3 text-slate-600 leading-relaxed">{p.text}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <FinalCTA />
      </main>
      <Footer />
      <WhatsAppFloat />
      <Toaster position="top-right" />
    </>
  );
}
